import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';

// Real people per UK day from the CloudFront logs (app/services/visitor_stats.py). Days not yet
// counted are filled in by the server in the background, so the chart re-polls while any are pending.
const BAR = '#2a78d6';
const INK = '#2d2d2d';
const MUTED = '#6b6b6b';
const GRID = '#e5e3de';

const shortDay = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const longDay = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const list = (obj) => Object.entries(obj || {}).map(([k, n]) => `${k} ${n}`).join(', ');

function DayTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: '#fff', border: `1px solid ${GRID}`, borderRadius: 8, padding: '8px 10px', fontSize: 12, color: INK, maxWidth: 260 }}>
      <div><strong style={{ fontSize: 14 }}>{d.people ?? '-'}</strong> {d.people === 1 ? 'person' : 'people'}</div>
      <div style={{ color: MUTED, marginBottom: 4 }}>{longDay(d.day)}</div>
      {d.people === null && <div style={{ color: MUTED }}>Still being counted</div>}
      {list(d.sources) && <div><span style={{ color: MUTED }}>From:</span> {list(d.sources)}</div>}
      {list(d.tools) && <div><span style={{ color: MUTED }}>Tools:</span> {list(d.tools)}</div>}
    </div>
  );
}

export default function VisitorChart() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [polls, setPolls] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.request('/admin/visitors?days=30')
      .then((res) => { if (!cancelled) { setData(res); setError(''); } })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not load visitors'); });
    return () => { cancelled = true; };
  }, [polls]);

  // While days are still being counted, check again every 10s (about a minute for a full backfill)
  useEffect(() => {
    if (!data?.pending || polls >= 18) return undefined;
    const t = setTimeout(() => setPolls((p) => p + 1), 10000);
    return () => clearTimeout(t);
  }, [data, polls]);

  if (error) return <p style={{ color: MUTED, fontSize: 13 }}>Visitors unavailable: {error}</p>;
  if (!data) return <p style={{ color: MUTED, fontSize: 13 }}>Loading visitors...</p>;

  const counted = data.days.filter((d) => d.people !== null).length;
  return (
    <div>
      <p style={{ fontSize: 13, color: MUTED, margin: '0 0 0.75rem' }}>
        <strong style={{ color: INK, fontSize: 15 }}>{data.total}</strong> people over {counted} days,{' '}
        <strong style={{ color: INK }}>{data.average ?? '-'}</strong> a day. Bots, scanners and your own visits are left out.
        {data.pending > 0 && ` Counting ${data.pending} more day${data.pending === 1 ? '' : 's'}...`}
      </p>
      <div style={{ width: '100%', height: 220 }} role="img"
           aria-label={`Daily visitors, ${data.total} people over ${counted} days`}>
        <ResponsiveContainer>
          <BarChart data={data.days} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barCategoryGap={2}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fontSize: 11, fill: MUTED }}
                   tickLine={false} axisLine={{ stroke: GRID }} interval="preserveStartEnd" minTickGap={24} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: MUTED }} tickLine={false} axisLine={false} width={44} />
            <Tooltip content={<DayTooltip />} cursor={{ fill: 'rgba(42,120,214,0.08)' }} />
            <Bar dataKey="people" fill={BAR} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <details style={{ marginTop: 8, fontSize: 12, color: MUTED }}>
        <summary style={{ cursor: 'pointer' }}>Show as a table</summary>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6, color: INK }}>
          <thead>
            <tr style={{ textAlign: 'left', color: MUTED }}>
              <th style={{ padding: '4px 6px' }}>Day</th><th style={{ padding: '4px 6px' }}>People</th>
              <th style={{ padding: '4px 6px' }}>From</th><th style={{ padding: '4px 6px' }}>Tools</th>
            </tr>
          </thead>
          <tbody>
            {[...data.days].reverse().map((d) => (
              <tr key={d.day} style={{ borderTop: `1px solid ${GRID}` }}>
                <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{longDay(d.day)}</td>
                <td style={{ padding: '4px 6px' }}>{d.people ?? '...'}</td>
                <td style={{ padding: '4px 6px' }}>{list(d.sources)}</td>
                <td style={{ padding: '4px 6px' }}>{list(d.tools)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}
